import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Star, Sparkles, Filter, Search, Plus, Trash2, Edit, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight, ChevronDown, X, History, ImageOff, FileText, Download } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import ArticleModal from '../../components/articles/ArticleModal';

// STAGE 3.4: CORRECTING API CALLS for bulk actions.

export default function ArticleManagementPage() {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [viewScope, setViewScope] = useState(() => localStorage.getItem('globalAdminViewScope') || 'mine');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({ auditStatus: 'ALL', publishStatus: 'ALL', isSeed: 'ALL', searchTerm: '' });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedArticles, setSelectedArticles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [currentArticle, setCurrentArticle] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchQuery = params.get('search');
    if (searchQuery) {
      setFilters(prev => ({ ...prev, searchTerm: searchQuery }));
    }
  }, [location.search]);


  const handleViewDetails = (article) => {
    setCurrentArticle(article);
    setModalMode('view');
    setIsModalOpen(true);
  };

  const handleEditArticle = (article) => {
    setCurrentArticle(article);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleSaveArticle = async (updatedData) => {
    try {
        await axios.put(`/api/articles/${updatedData.id}`, updatedData);
        fetchArticles();
        setIsModalOpen(false);
    } catch (error) {
        console.error("Failed to update article:", error);
        alert("更新文章失败");
    }
  };

  const handleGenerateImage = (article) => {
    // Navigate to ImageManagement and pre-fill search with article title
    if (article && article.title) {
        navigate(`/content/images?search=${encodeURIComponent(article.title)}`);
    } else {
        navigate('/content/images');
    }
  };


  useEffect(() => {
    localStorage.setItem('globalAdminViewScope', viewScope);
    fetchArticles();
  }, [viewScope]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/articles', { params: { view_scope: viewScope } });
      setArticles(response.data);
    } catch (err) { setError('无法加载文章列表。'); } 
    finally { setLoading(false); }
  };

  const handleFilterChange = (e) => { const { name, value } = e.target; setFilters(prev => ({ ...prev, [name]: value })); setCurrentPage(1); };
  const handleSearchChange = (e) => { setFilters(prev => ({...prev, searchTerm: e.target.value})); setCurrentPage(1); };
  const resetFilters = () => { setFilters({ auditStatus: 'ALL', publishStatus: 'ALL', isSeed: 'ALL', searchTerm: '' }); setCurrentPage(1); };
  
  const handleSelectArticle = (id) => { setSelectedArticles(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]); };
  const handleSelectAll = (e) => {
    if (e.target.checked) { setSelectedArticles(paginatedArticles.map(a => a.id)); } 
    else { setSelectedArticles([]); }
  };
  
  // CORRECTED API CALLS
  const handleBulkAction = async (action, value) => {
      try {
          if (action === 'download_excel' || action === 'download_csv') {
              const format = action === 'download_excel' ? 'excel' : 'csv';
              const response = await axios.post('/api/articles/bulk/download', { ids: selectedArticles, format }, { responseType: 'blob' });
              
              const url = window.URL.createObjectURL(new Blob([response.data]));
              const link = document.createElement('a');
              link.href = url;
              link.setAttribute('download', `articles_export_${new Date().getTime()}.zip`);
              document.body.appendChild(link);
              link.click();
              link.parentNode.removeChild(link);
              
              return;
          }

          await axios.put('/api/articles/bulk/action', { ids: selectedArticles, action, value });
          fetchArticles();
          setSelectedArticles([]);
      } catch (error) {
          console.error(`Failed to perform bulk action ${action}:`, error);
          alert(`批量操作失败: ${action}`);
      }
  };

  
  
  
  const handleAnalyze = async (article) => {
    if (!article.isSeed) {
        alert("只有标为优质的文章才能提取模板！");
        return;
    }
    if (!window.confirm(`确认要提取优质文章 "${article.title}" 的内容结构作为模板吗？`)) return;
    try {
        const response = await axios.post(`${API_URL}/articles/${article.id}/analyze-template`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        alert(`模板提取成功！(模板名: ${response.data.template.name})`);
    } catch (error) {
        console.error("Failed to analyze template:", error);
        alert("提取模板失败，请检查后端服务！");
    }
  };

  const handleGenerate = async (article) => {
    if (!article.isSeed) {
        alert("只有标为优质的文章才能进行一键生成！");
        return;
    }
    
    if (!window.confirm(`确认要基于优质文章 "${article.title}" 一键生成新图文吗？`)) return;

    try {
        const response = await axios.post(`${API_URL}/articles/${article.id}/generate`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
        alert(`生成任务已提交 (任务ID: ${response.data.taskId})！请前往「任务管理」查看进度。`);
    } catch (error) {
        console.error("Failed to start generation task:", error);
        alert("提交生成任务失败，请检查后端服务！");
    }
  };
  const handleToggleSeed = async (articleId, isSeed) => {
    try {
        await axios.put(`/api/articles/${articleId}`, { isSeed: !isSeed });
        fetchArticles();
    } catch (error) { console.error("Failed to toggle seed status:", error); }
  };

  const filteredArticles = useMemo(() => articles.filter(article => (filters.auditStatus === 'ALL' || article.auditStatus === filters.auditStatus) && (filters.publishStatus === 'ALL' || article.publishStatus === filters.publishStatus) && (filters.isSeed === 'ALL' || String(article.isSeed) === filters.isSeed) && (article.title.toLowerCase().includes(filters.searchTerm.toLowerCase())) ), [articles, filters]);
  const paginatedArticles = useMemo(() => { const startIndex = (currentPage - 1) * itemsPerPage; return filteredArticles.slice(startIndex, startIndex + itemsPerPage); }, [filteredArticles, currentPage, itemsPerPage]);
  const totalPages = Math.ceil(filteredArticles.length / itemsPerPage);
  const isAllOnPageSelected = paginatedArticles.every(a => selectedArticles.includes(a.id)) && paginatedArticles.length > 0;

  return (
    <div className="p-6 bg-gray-50 min-h-full">
      <Header isFilterOpen={isFilterOpen} setIsFilterOpen={setIsFilterOpen} viewScope={viewScope} setViewScope={setViewScope} user={user} />
      <ActionBar 
        selectedCount={selectedArticles.length} 
        onBulkAction={handleBulkAction}
      />
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        <FilterBar isFilterOpen={isFilterOpen} filters={filters} onFilterChange={handleFilterChange} onSearchChange={handleSearchChange} onResetFilters={resetFilters}/>
        <ArticleTable loading={loading} error={error} articles={paginatedArticles} selectedArticles={selectedArticles} isAllSelected={isAllOnPageSelected} onSelectArticle={handleSelectArticle} onSelectAll={handleSelectAll} onToggleSeed={handleToggleSeed} onBulkAction={handleBulkAction} onViewDetails={handleViewDetails} onEdit={handleEditArticle} onGenerate={handleGenerate} />
        <ArticleModal 
          isOpen={isModalOpen} 
          mode={modalMode} 
          onClose={() => setIsModalOpen(false)} 
          onModeChange={setModalMode}
          onSave={handleSaveArticle} 
          onGenerateImage={handleGenerateImage} 
          articleData={currentArticle} 
          onDelete={(id) => handleBulkAction('delete', [id])}
        />
        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredArticles.length} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage}/>
      </div>
    </div>
  );
}

const Header = ({ isFilterOpen, setIsFilterOpen, viewScope, setViewScope, user }) => (
  <div className="flex justify-between items-center mb-6">
    <div className="flex items-center gap-6">
      <h1 className="text-2xl font-semibold flex items-center gap-2"><FileText className="w-6 h-6 text-indigo-600" />文章管理</h1>
      {user?.role === 'ADMIN' && (
        <div className="flex bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setViewScope('mine')}
            className={`px-4 py-1.5 text-sm rounded-md transition-all ${viewScope === 'mine' ? 'bg-white text-blue-600 shadow-sm font-medium' : 'text-gray-600 hover:text-gray-900'}`}
          >
            我的内容
          </button>
          <button
            onClick={() => setViewScope('all')}
            className={`px-4 py-1.5 text-sm rounded-md transition-all ${viewScope === 'all' ? 'bg-white text-blue-600 shadow-sm font-medium' : 'text-gray-600 hover:text-gray-900'}`}
          >
            全部内容
          </button>
        </div>
      )}
    </div>
    <div className="flex gap-2">
      <Link to="/content/article-recycle-bin" className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium text-gray-600"><History className="w-4 h-4" />回收站</Link>
      <button onClick={() => setIsFilterOpen(!isFilterOpen)} className={`flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-sm font-medium transition-colors ${isFilterOpen ? 'bg-gray-50 border-gray-300 shadow-inner' : ''}`}><Filter className="w-4 h-4" />筛选</button>
      <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm font-medium shadow-sm"><Plus className="w-4 h-4" />新建</button>
    </div>
  </div>
);
const ActionBar = ({ selectedCount, onBulkAction }) => { 
    if (selectedCount === 0) return null; 
    return (
        <div className="flex items-center justify-between bg-indigo-50 border border-indigo-200 rounded-lg px-4 py-2 mb-4 text-sm">
            <span className="font-medium text-indigo-700">{selectedCount}篇文章已选择</span>
            <div className="flex items-center gap-2">
                <button onClick={() => onBulkAction('mark_seed')} className="flex items-center gap-1.5 px-3 py-1.5 bg-yellow-400 text-white rounded-md hover:bg-yellow-500 text-xs font-semibold shadow-sm"><Star className="w-3 h-3"/>标为优质</button>
                <button onClick={() => onBulkAction('unmark_seed')} className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-400 text-white rounded-md hover:bg-gray-500 text-xs font-semibold shadow-sm"><X className="w-3 h-3"/>取消优质</button>
                <div className="relative group inline-block">
                    <button className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 text-xs font-semibold shadow-sm"><Download className="w-3 h-3"/>批量下载<ChevronDown className="w-3 h-3"/></button>
                    <div className="absolute hidden group-hover:block z-50 w-24 bg-white shadow-lg border border-gray-100 rounded-md top-full left-0 mt-1">
                       <button onClick={() => onBulkAction('download_excel')} className="block w-full text-left px-3 py-2 text-xs hover:bg-gray-50 text-gray-700">Excel</button>
                       <button onClick={() => onBulkAction('download_csv')} className="block w-full text-left px-3 py-2 text-xs hover:bg-gray-50 text-gray-700">CSV</button>
                    </div>
                </div>
                <button onClick={() => window.confirm(`您确定要将 ${selectedCount} 篇文章移至回收站吗？`) && onBulkAction('delete')} className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 text-white rounded-md hover:bg-red-600 text-xs font-semibold shadow-sm"><Trash2 className="w-3 h-3"/>批量删除</button>
            </div>
        </div>
    );
};
const FilterBar = ({ isFilterOpen, filters, onFilterChange, onSearchChange, onResetFilters }) => ( <div className="p-4 border-b border-gray-100 flex flex-col gap-4"><div className="relative max-w-md w-full"><Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" /><input type="text" placeholder="按标题搜索..." value={filters.searchTerm} onChange={onSearchChange} className="w-full pl-9 pr-4 py-2 bg-gray-50 border-none rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 outline-none transition-shadow"/></div>{isFilterOpen && ( <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-3 border-t border-gray-100 animate-in fade-in slide-in-from-top-2 duration-200"><FilterSelect name="auditStatus" value={filters.auditStatus} onChange={onFilterChange} label="审核状态" options={{ALL: "全部", APPROVED: "采纳", PENDING: "待定"}} /><FilterSelect name="publishStatus" value={filters.publishStatus} onChange={onFilterChange} label="发布状态" options={{ALL: "全部", PUBLISHED: "已发布", UNPUBLISHED: "未发布"}} /><FilterSelect name="isSeed" value={filters.isSeed} onChange={onFilterChange} label="文章类型" options={{ALL: "全部", true: "优质", false: "普通"}} /><div className="flex items-end"><button onClick={onResetFilters} className="w-full px-4 py-2 text-sm bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors">重置</button></div></div>)}</div>);
const FilterSelect = ({ name, value, onChange, label, options }) => ( <div><label className="text-xs font-medium text-gray-500 mb-1">{label}</label><select name={name} value={value} onChange={onChange} className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none">{Object.entries(options).map(([val, text]) => <option key={val} value={val}>{text}</option>)}</select></div>);

const ArticleTable = ({ loading, error, articles, selectedArticles, isAllSelected, onSelectArticle, onSelectAll, onToggleSeed, onBulkAction, onViewDetails, onEdit, onGenerate }) => {
    const getStatusPill = (status, type) => {
        if (!status) return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">未知</span>;
        const styles = { audit: { APPROVED: "bg-green-100 text-green-700", PENDING: "bg-yellow-100 text-yellow-700", REJECTED: "bg-red-100 text-red-700" }, publish: { PUBLISHED: "bg-blue-100 text-blue-700", UNPUBLISHED: "bg-gray-100 text-gray-700" } };
        const text = { audit: { APPROVED: "采纳", PENDING: "待定", REJECTED: "废弃" }, publish: { PUBLISHED: "已发布", UNPUBLISHED: "未发布"} };
        return <span className={`px-2 py-1 text-xs font-medium rounded-full ${styles[type]?.[status]}`}>{text[type]?.[status] || '未知'}</span>;
    };
    if (loading) return <div className="p-8 text-center text-gray-500">加载中...</div>;
    if (error) return <div className="p-8 text-center text-red-500">{error}</div>;
    if (articles.length === 0) return <div className="p-8 text-center text-gray-500">没有找到匹配的文章。</div>;
    return (
        <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
                <thead className="bg-gray-50/80 text-gray-500 text-xs uppercase border-b border-gray-100">
                    <tr>
                        <th className="px-4 py-3 w-12 text-center"><input type="checkbox" checked={isAllSelected} onChange={onSelectAll} className="rounded border-gray-300"/></th>
                        <th className="px-6 py-3">封面图</th><th className="px-2 py-3">标题</th><th className="px-2 py-3 w-12 text-center">优质</th>
                        <th className="px-6 py-3">审核状态</th><th className="px-6 py-3">发布状态</th><th className="px-6 py-3">生成时间</th><th className="px-6 py-3 text-right">操作</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {articles.map(article => (
                        <tr key={article.id} className={`cursor-pointer ${selectedArticles.includes(article.id) ? 'bg-indigo-50' : 'hover:bg-gray-50/80'}`} onClick={() => onViewDetails(article)}>
                            <td className="px-4 py-2 text-center"><input type="checkbox" checked={selectedArticles.includes(article.id)} onChange={() => onSelectArticle(article.id)} onClick={e => e.stopPropagation()} className="rounded border-gray-300"/></td>
                            <td className="px-6 py-2">{article.coverImage ? <img src={article.coverImage.startsWith('http') ? article.coverImage : `/api/thumb?url=${encodeURIComponent(article.coverImage)}&w=200&h=130`} alt={article.title} className="w-24 h-16 object-cover rounded-md bg-gray-100" loading="lazy" /> : <div className="w-24 h-16 flex flex-col items-center justify-center text-gray-400 bg-gray-100 rounded-md border border-gray-200 border-dashed"><ImageOff className="w-5 h-5 mb-1 opacity-50"/><span className="text-[10px] uppercase font-bold tracking-wider opacity-60">无封面</span></div>}</td>
                            <td className="px-2 py-2 font-semibold max-w-xs truncate hover:text-indigo-600 transition-colors" title={article.title} onClick={(e) => { e.stopPropagation(); onViewDetails(article); }}>{article.title}</td>
                            <td className="px-2 py-2 text-center"><button onClick={(e) => {e.stopPropagation(); onToggleSeed(article.id, article.isSeed)}} className="p-2"><Star className={`w-5 h-5 ${article.isSeed ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} /></button></td>
                            <td className="px-6 py-2">{getStatusPill(article.auditStatus, 'audit')}</td><td className="px-6 py-2">{getStatusPill(article.publishStatus, 'publish')}</td>
                            <td className="px-6 py-2 text-gray-500">{new Date(article.createdAt).toLocaleDateString()}</td>
                            <td className="px-6 py-2 text-right">
                                <div className="flex items-center justify-end gap-1">
                                    {article.isSeed && (
                                        <>
                                            <button onClick={(e) => { e.stopPropagation(); onGenerate(article); }} className="p-2 text-indigo-500 hover:text-indigo-700" title="一键生成新内容"><Sparkles className="w-4 h-4" /></button>
                                            <button onClick={(e) => { e.stopPropagation(); onAnalyze(article); }} className="p-2 text-emerald-500 hover:text-emerald-700" title="提取结构模板"><FileText className="w-4 h-4" /></button>
                                        </>
                                    )}
                                    <button onClick={(e) => { e.stopPropagation(); onEdit(article); }} className="p-2 text-gray-400 hover:text-blue-600" title="编辑"><Edit className="w-4 h-4" /></button>
                                    <button onClick={(e) => { e.stopPropagation(); onBulkAction('delete', [article.id]) }} className="p-2 text-gray-400 hover:text-red-600" title="移至回收站"><Trash2 className="w-4 h-4" /></button>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
const Pagination = ({ currentPage, totalPages, onPageChange, totalItems, itemsPerPage, setItemsPerPage }) => { if (totalPages <= 1) return null; return ( <div className="flex items-center justify-between p-4 border-t text-sm"><div className="flex items-center gap-2"><span>每页</span><select value={itemsPerPage} onChange={(e) => setItemsPerPage(Number(e.target.value))} className="px-2 py-1 border rounded-md"><option value={10}>10</option><option value={20}>20</option><option value="50">50</option></select><span>条，共 {totalItems} 条</span></div><div className="flex items-center gap-1"><button onClick={() => onPageChange(1)} disabled={currentPage === 1} className="p-2 disabled:opacity-50"><ChevronsLeft className="w-4 h-4"/></button><button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} className="p-2 disabled:opacity-50"><ChevronLeft className="w-4 h-4"/></button><span className="px-3 py-1">第 {currentPage} / {totalPages} 页</span><button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-2 disabled:opacity-50"><ChevronRight className="w-4 h-4"/></button><button onClick={() => onPageChange(totalPages)} disabled={currentPage === totalPages} className="p-2 disabled:opacity-50"><ChevronsRight className="w-4 h-4"/></button></div></div>);};
